import { useEffect, useRef } from 'react';
import SignaturePad from 'signature_pad';

/**
 * Custom hook that manages a SignaturePad instance on a canvas element.
 *
 * Uses a ref for the onSignatureChange callback so the initialization effect
 * only runs once on mount, avoiding unnecessary teardown/reinit on re-renders.
 *
 * @param {object} options
 * @param {function} options.onSignatureChange - Called with base64 PNG data URL when a stroke ends
 * @returns {{ canvasRef: React.RefObject, clearSignature: function }}
 */
export default function useSignaturePad({ onSignatureChange }) {
    const canvasRef = useRef(null);
    const signaturePadRef = useRef(null);

    // Keep a stable ref to the callback so the setup effect doesn't need it as a dep
    const onSignatureChangeRef = useRef(onSignatureChange);
    useEffect(() => {
        onSignatureChangeRef.current = onSignatureChange;
    });

    // Initialize SignaturePad once on mount
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Resize canvas to match display size with device pixel ratio
        const resizeCanvas = () => {
            const ratio = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();

            canvas.width = rect.width * ratio;
            canvas.height = rect.height * ratio;

            const ctx = canvas.getContext('2d');
            ctx.scale(ratio, ratio);

            canvas.style.width = `${rect.width}px`;
            canvas.style.height = `${rect.height}px`;

            if (signaturePadRef.current) {
                signaturePadRef.current.clear();
            }
        };

        const initSignaturePad = () => {
            resizeCanvas();

            signaturePadRef.current = new SignaturePad(canvas, {
                penColor: 'black',
                minWidth: 1,
                maxWidth: 3,
            });

            signaturePadRef.current.addEventListener('endStroke', () => {
                if (
                    signaturePadRef.current &&
                    !signaturePadRef.current.isEmpty()
                ) {
                    const signatureData =
                        signaturePadRef.current.toDataURL('image/png');
                    onSignatureChangeRef.current(signatureData);
                }
            });
        };

        // Use requestAnimationFrame to ensure the canvas is laid out before init
        requestAnimationFrame(initSignaturePad);

        window.addEventListener('resize', resizeCanvas);
        window.addEventListener('orientationchange', resizeCanvas);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('orientationchange', resizeCanvas);
            if (signaturePadRef.current) {
                signaturePadRef.current.off();
                signaturePadRef.current = null;
            }
        };
    }, []); // mount only — all external deps are refs (stable by definition)

    /**
     * Clear all strokes from the canvas.
     */
    const clearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
    };

    return { canvasRef, clearSignature };
}
